import React from 'react';
import { Tooltip } from 'antd';

import {
  ageInDays,
  parseDisplayDate,
} from '@totem/components/common/dateDisplay/dateDisplayUtils';

export type AgeStatus = 'fresh' | 'aging' | 'urgent' | 'critical';

export const ageStatus = (days: number): AgeStatus =>
  days < 7 ? 'fresh' : days < 30 ? 'aging' : days < 60 ? 'urgent' : 'critical';

export const AGE_STATUS_STYLE: Record<
  AgeStatus,
  { background: string; color: string }
> = {
  fresh: { background: '#DCF5E6', color: '#22c55e' },
  aging: { background: '#FCEFD3', color: '#f59e0b' },
  urgent: { background: '#FBE0E0', color: '#ef4444' },
  critical: { background: '#F7DCDC', color: '#b91c1c' },
};

export const AGE_STATUS_OPTIONS = [
  { value: 'fresh', label: 'Fresh (< 7d)' },
  { value: 'aging', label: 'Aging (7–30d)' },
  { value: 'urgent', label: 'Urgent (30–60d)' },
  { value: 'critical', label: 'Critical (60d+)' },
];

type Props = {
  date: string;
};

// Renders the elapsed days since `date` (e.g. "12.4d") as a pill colored by
// age bucket: fresh < 7d, aging < 30d, urgent < 60d, critical 60d+.
const AgeDisplay = ({ date }: Props) => {
  const parsed = parseDisplayDate(date);
  if (parsed === null) {
    return <span>-</span>;
  }

  const days = ageInDays(parsed);
  const style = AGE_STATUS_STYLE[ageStatus(days)];

  return (
    <Tooltip title={parsed.toLocaleString()}>
      <span
        style={{
          ...style,
          padding: '2px 7px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {days.toFixed(1)}d
      </span>
    </Tooltip>
  );
};

export default AgeDisplay;
